import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { cardClass } from '@app/constants/styles';
import { EditSheet } from '@app/components/core/edit-sheet';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { useListSprints } from '@data/hooks/sprints';

import { asAmount, formatEur } from './budget';
import { PRICING_MODES, fixedShare, sprintsOfVersion, type PricingMode, type VersionPricing } from './pricing';
import type { ProductVersion } from './productVersions';
import { readRelease, readRetention, typedPercent, writeRetention } from './retention';
import { useReportUnsaved } from './unsavedWork';

const termClass = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:w-44 sm:shrink-0 sm:pt-2';
// One height for a value and the field that replaces it, so opening Edit moves nothing.
const lineClass = 'flex min-h-8 flex-wrap items-center gap-2 text-sm';

const Row = ({ term, children }: { term: string; children: ReactNode }): ReactElement => (
  <div className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0 sm:flex-row sm:gap-6">
    <dt className={termClass}>{term}</dt>
    <dd className="min-w-0 flex-1">{children}</dd>
  </div>
);

/** Money rides on it, so it reads until Edit and changes only on Save; editing keeps every row where it was. */
const VersionPricingCard = ({
  productId,
  version,
  pricing,
  canEdit,
  onChange,
}: {
  productId: string;
  version?: ProductVersion;
  pricing: VersionPricing;
  canEdit: boolean;
  onChange: (pricing: VersionPricing) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const { data: sprintList } = useListSprints({ limit: 400 });
  const versionSprints = useMemo(
    () => (version ? sprintsOfVersion(version, sprintList?.items ?? []).length : 0),
    [version, sprintList],
  );
  // Bumped on save, because the retention lives outside React in the product store.
  const [revision, setRevision] = useState(0);
  const retention = useMemo(
    () => (version ? readRetention(productId, version.number) : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productId, version, revision],
  );

  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<PricingMode>(pricing.mode);
  const [total, setTotal] = useState('');
  const [sprints, setSprints] = useState('');
  const [typedRetention, setTypedRetention] = useState('');
  const [typedMarkup, setTypedMarkup] = useState('');

  const startEditing = (): void => {
    setMode(pricing.mode);
    setTotal(pricing.total === undefined ? '' : String(pricing.total));
    // A fixed price starts spread over every sprint the version has, the usual contract.
    const count = pricing.sprints ?? (versionSprints || undefined);
    setSprints(count === undefined ? '' : String(count));
    setTypedRetention(retention ? String(retention) : '');
    setTypedMarkup(pricing.markup === undefined ? '' : String(pricing.markup));
    setEditing(true);
  };
  // An edit belongs to the version it was opened on.
  useEffect(() => setEditing(false), [version?.number]);

  const typedTotal = total.trim() === '' ? null : asAmount(total);
  const typedSprints = /^\d+$/.test(sprints.trim()) && Number(sprints) > 0 ? Number(sprints) : null;
  const fixedValid = typedTotal !== null && typedTotal > 0 && typedSprints !== null;
  const enteredRetention = typedPercent(typedRetention);
  // Released money was paid out of what the old percentage held; lowering it would bill that twice.
  // The year pools what is held, so a release into another version's sprint came out of this one too.
  const releasedInVersion = useMemo(
    () =>
      version
        ? (sprintList?.items ?? [])
            .filter(sprint => sprint.startDate.slice(0, 4) === version.startDate.slice(0, 4))
            .some(sprint => readRelease(productId, sprint.id) > 0)
        : false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productId, version, sprintList, revision],
  );
  const loweredAfterRelease = releasedInVersion && enteredRetention !== null && enteredRetention < retention;
  // Required: an at-cost deal always names its markup, 0% included, so a blank is not a choice.
  const enteredMarkup = typedMarkup.trim() === '' ? null : asAmount(typedMarkup);
  const markupValid = enteredMarkup !== null && enteredMarkup <= 1000;
  const toSave = (): VersionPricing => {
    if (mode === 'fixedPrice') return { mode, total: typedTotal ?? undefined, sprints: typedSprints ?? undefined };
    if (mode === 'atCost') return { mode, markup: enteredMarkup ?? undefined };
    return { mode };
  };
  const next = toSave();
  const dirty =
    next.mode !== pricing.mode ||
    (mode === 'fixedPrice' && (next.total !== pricing.total || next.sprints !== pricing.sprints)) ||
    (mode === 'atCost' && next.markup !== pricing.markup) ||
    enteredRetention !== retention;
  // The model prices the pool a release came out of as much as the percentage does, so it waits too.
  const repricedAfterRelease =
    releasedInVersion &&
    (next.mode !== pricing.mode ||
      (mode === 'fixedPrice' && (next.total !== pricing.total || next.sprints !== pricing.sprints)) ||
      (mode === 'atCost' && next.markup !== pricing.markup));
  const canSave =
    dirty &&
    (mode !== 'fixedPrice' || fixedValid) &&
    (mode !== 'atCost' || markupValid) &&
    enteredRetention !== null &&
    !loweredAfterRelease &&
    !repricedAfterRelease;
  useReportUnsaved('versionPricing', editing && dirty);

  const save = (): void => {
    if (!canSave || !version) return;
    onChange(next);
    if (enteredRetention !== retention) {
      writeRetention(productId, version.number, enteredRetention as number);
      setRevision(current => current + 1);
    }
    setEditing(false);
  };

  const savedMarkup =
    pricing.markup === undefined ? (
      <span className="text-muted-foreground">{t('pricing.noMarkup')}</span>
    ) : (
      <span className="text-foreground">{t('pricing.markupSummary', { percent: pricing.markup })}</span>
    );
  const savedFixed =
    pricing.total && pricing.sprints ? (
      <span className="text-foreground">
        {t('pricing.fixedSummary', {
          total: formatEur(pricing.total),
          count: pricing.sprints,
          amount: formatEur(fixedShare(pricing.total, pricing.sprints, 0)),
        })}
      </span>
    ) : (
      <span className="text-muted-foreground">{t('pricing.noFixedTotal')}</span>
    );
  const modeItems = selectItemsByValue(PRICING_MODES.map(option => ({ value: option, label: t(`pricing.${option}`) })));

  const modeShown = (edit: boolean): PricingMode => (edit ? mode : pricing.mode);
  const rows = (edit: boolean): ReactElement => (
    <dl className="divide-y divide-border">
      <Row term={t('pricing.model')}>
        <div className={lineClass}>
          {edit ? (
            <Select items={modeItems} value={mode} onValueChange={value => value && setMode(value as PricingMode)}>
              <SelectTrigger size="sm" aria-label={t('pricing.model')} className="w-48 bg-popover">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start" alignItemWithTrigger={false}>
                {PRICING_MODES.map(option => (
                  <SelectItem key={option} value={option}>
                    {t(`pricing.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="font-medium text-foreground">{t(`pricing.${pricing.mode}`)}</span>
          )}
          {/* On the model's own line, so it reads as one phrase: "At cost plus 15% markup". */}
          {modeShown(edit) === 'atCost' &&
            (edit ? (
              <>
                <span className="text-foreground">{t('pricing.plus')}</span>
                <Input
                  aria-label={t('pricing.markup')}
                  inputMode="decimal"
                  value={typedMarkup}
                  placeholder="0"
                  onChange={event => setTypedMarkup(event.target.value)}
                  aria-invalid={typedMarkup.trim() !== '' && !markupValid}
                  className="h-8 w-16 bg-popover text-right tabular-nums"
                />
                <span className="text-foreground">{t('pricing.markupSuffix')}</span>
              </>
            ) : (
              savedMarkup
            ))}
          {modeShown(edit) === 'fixedPrice' &&
            (edit ? (
              <>
                <span className="text-foreground">{t('pricing.of')} €</span>
                <Input
                  aria-label={t('pricing.total')}
                  inputMode="decimal"
                  value={total}
                  placeholder="0"
                  onChange={event => setTotal(event.target.value)}
                  aria-invalid={total.trim() !== '' && typedTotal === null}
                  className="h-8 w-32 bg-popover text-right tabular-nums"
                />
                <span className="text-foreground">{t('pricing.over')}</span>
                <Input
                  aria-label={t('pricing.sprints')}
                  inputMode="numeric"
                  value={sprints}
                  onChange={event => setSprints(event.target.value)}
                  aria-invalid={sprints.trim() !== '' && typedSprints === null}
                  className="h-8 w-16 bg-popover text-right tabular-nums"
                />
                <span className="text-foreground">{t('pricing.sprintsUnit', { count: typedSprints ?? 0 })}</span>
                {fixedValid ? (
                  <span className="text-muted-foreground">
                    {t('pricing.perSprintAside', {
                      amount: formatEur(fixedShare(typedTotal as number, typedSprints as number, 0)),
                    })}
                  </span>
                ) : null}
              </>
            ) : (
              savedFixed
            ))}
        </div>
        <p className="text-sm text-muted-foreground">{t(`pricing.${modeShown(edit)}Hint`)}</p>
        {edit && repricedAfterRelease ? <p className="text-xs text-destructive">{t('pricing.releasedFirst')}</p> : null}
      </Row>

      <Row term={t('retention.title')}>
        <div className={lineClass}>
          {edit ? (
            <>
              <Input
                aria-label={t('retention.title')}
                inputMode="decimal"
                value={typedRetention}
                placeholder="0"
                onChange={event => setTypedRetention(event.target.value)}
                aria-invalid={enteredRetention === null}
                className="h-8 w-20 bg-popover text-right tabular-nums"
              />
              <span className="text-muted-foreground">%</span>
              {enteredRetention === null ? (
                <span className="text-xs text-destructive">{t('retention.invalid')}</span>
              ) : null}
              {loweredAfterRelease ? (
                <span className="text-xs text-destructive">{t('retention.releasedFirst')}</span>
              ) : null}
            </>
          ) : (
            <span className="font-medium text-foreground">
              {retention > 0 ? `${retention}%` : t('retention.noneShort')}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{t('retention.fieldHint')}</p>
      </Row>
    </dl>
  );

  const isMobile = useIsMobile();
  // A phone edits in a sheet (below), leaving the card as it reads.
  const inline = editing && !isMobile;

  return (
    <>
      <form
        className={cardClass}
        onSubmit={event => {
          event.preventDefault();
          save();
        }}
      >
        <div className="mb-4 flex min-h-8 items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-foreground">{t('pricing.title')}</h2>
          {/* In the Edit button's place, so the header keeps its height either way. */}
          {inline ? (
            <div className="flex shrink-0 gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" size="sm" disabled={!canSave}>
                {t('actions.save')}
              </Button>
            </div>
          ) : (
            canEdit &&
            version && (
              <Button type="button" variant="outline" size="sm" onClick={startEditing}>
                <Pencil className="size-4" />
                {t('edit')}
              </Button>
            )
          )}
        </div>

        {rows(inline)}
      </form>
      <EditSheet
        open={editing && isMobile}
        title={t('pricing.title')}
        onCancel={() => setEditing(false)}
        onSubmit={event => {
          event.preventDefault();
          save();
        }}
        canSave={canSave}
      >
        {rows(true)}
      </EditSheet>
    </>
  );
};

export default VersionPricingCard;
